import { DeviceEventEmitter, NativeEventEmitter, NativeModules, AppState, AppStateStatus, Platform } from 'react-native';

export enum VoicePlayState {
	None = 'None',
	Playing = 'Playing',
	Ended = 'Ended',
	Error = 'Error',
}

export type VoicePlayEvent = {
	mid: string;
	state: VoicePlayState;
};

export type VoicePlayListener = (event: VoicePlayEvent) => void;

/**
 * 语音播放管理器单例
 * 负责管理语音播放状态，通过广播的方式通知动画控制
 */
class VoicePlayManager {
	private static instance: VoicePlayManager;
	private playState: VoicePlayState = VoicePlayState.None;
	private currentMid: string | null = null;
	private listeners: VoicePlayListener[] = [];
	private eventEmitter: NativeEventEmitter | null = null;
	private appStateListener: any = null;
	// Web 平台使用 HTMLAudioElement
	private webAudioElement: HTMLAudioElement | null = null;

	private constructor() {
		this.init();
	}

	public static getInstance(): VoicePlayManager {
		if (!VoicePlayManager.instance) {
			VoicePlayManager.instance = new VoicePlayManager();
		}
		return VoicePlayManager.instance;
	}

	/**
	 * 初始化管理器，设置原生事件监听
	 */
	private init() {
	}

	/**
	 * 销毁管理器，清理监听器
	 */
	public destroy() {
	}

	/**
	 * 处理播放完成事件
	 */
	private handlePlayEnd = () => {
		this.updatePlayState(VoicePlayState.Ended);
	};

	/**
	 * 处理播放错误事件
	 */
	private handlePlayError = () => {
		this.updatePlayState(VoicePlayState.Error);
	};

	/**
	 * 处理 App 状态变化
	 */
	private handleAppStateChange = (nextState: AppStateStatus) => {
		if (nextState === 'background' && this.playState === VoicePlayState.Playing) {
			this.stop();
		}
	};

	/**
	 * 处理 PlayerManager 状态变化
	 */
	private handlePlayerStateChange = (event: 'playing' | 'pause' | 'stop') => {
		if (event === 'pause' || event === 'stop') {
			this.updatePlayState(VoicePlayState.Ended);
		}
	};

	/**
	 * 更新播放状态并广播
	 */
	private updatePlayState(state: VoicePlayState) {
		this.playState = state;
		if (this.currentMid) {
			this.broadcast({
				mid: this.currentMid,
				state: state,
			});
		}
		if (state === VoicePlayState.Ended || state === VoicePlayState.Error) {
			this.currentMid = null;
		}
	}

	/**
	 * 广播播放事件
	 */
	private broadcast(event: VoicePlayEvent) {
		this.listeners.forEach((listener) => {
			try {
				listener(event);
			} catch (error) {
				console.error('VoicePlayManager broadcast error:', error);
			}
		});
	}

	/**
	 * 添加播放事件监听器
	 */
	public addListener(listener: VoicePlayListener) {
		if (!this.listeners.includes(listener)) {
			this.listeners.push(listener);
		}
	}

	/**
	 * 移除播放事件监听器
	 */
	public removeListener(listener: VoicePlayListener) {
		this.listeners = this.listeners.filter((l) => l !== listener);
	}

	/**
	 * 播放语音
	 * @param mid 消息ID
	 * @param url 语音文件URL
	 * @param outputType 输出类型：'Speaker' 外放，'Receiver' 听筒，'Default' 根据设置自动选择
	 */
	public async play(mid: string, url: string, outputType: 'Speaker' | 'Receiver' | 'Default' = 'Default'): Promise<boolean> {
		// 如果正在播放，先停止
		if (this.playState === VoicePlayState.Playing) {
			await this.stop();
			return false;
		}

		this.currentMid = mid;

		if (Platform.OS === 'web') {
			// Web 平台使用 HTMLAudioElement
			try {
				// 停止之前的播放
				if (this.webAudioElement) {
					this.webAudioElement.pause();
					this.webAudioElement = null;
				}

				// 创建新的音频元素
				this.webAudioElement = new Audio(url);
				
				// 设置事件监听
				this.webAudioElement.onended = () => {
					this.handlePlayEnd();
				};
				
				this.webAudioElement.onerror = () => {
					this.handlePlayError();
				};

				// 开始播放
				await this.webAudioElement.play();
				this.updatePlayState(VoicePlayState.Playing);
				return true;
			} catch (error) {
				console.error('VoicePlayManager play error:', error);
				this.handlePlayError();
				return false;
			}
		} else {
			// 移动端使用原生模块
			try {
				const AudioPlayer = NativeModules.BeeAudioPlayerModule || NativeModules.AudioPlayer;
				if (!AudioPlayer) {
					console.error('AudioPlayer module not found');
					return false;
				}

				// 初始化播放器（如果需要）
				if (AudioPlayer.initialize) {
					await AudioPlayer.initialize();
				}

				// 播放音频
				await AudioPlayer.play(url);
				this.updatePlayState(VoicePlayState.Playing);
				return true;
			} catch (error) {
				console.error('VoicePlayManager play error:', error);
				this.handlePlayError();
				return false;
			}
		}
	}

	/**
	 * 停止播放
	 */
	public async stop() {
		if (Platform.OS === 'web') {
			// Web 平台
			if (this.webAudioElement) {
				this.webAudioElement.pause();
				this.webAudioElement.currentTime = 0;
				this.webAudioElement = null;
			}
		} else {
			// 移动端
			try {
				const AudioPlayer = NativeModules.BeeAudioPlayerModule || NativeModules.AudioPlayer;
				if (AudioPlayer && AudioPlayer.stop) {
					await AudioPlayer.stop();
				}
			} catch (error) {
				console.error('VoicePlayManager stop error:', error);
			}
		}
		
		this.updatePlayState(VoicePlayState.Ended);
	}

	public async preloadUrl(url: string) {
	}

	public async clearCache() {
	}

	/**
	 * 获取当前播放状态
	 */
	public getPlayState(): VoicePlayState {
		return this.playState;
	}

	/**
	 * 获取当前播放的消息ID
	 */
	public getCurrentMid(): string | null {
		return this.currentMid;
	}

	/**
	 * 检查指定消息是否正在播放
	 */
	public isPlaying(mid: string): boolean {
		return this.playState === VoicePlayState.Playing && this.currentMid === mid;
	}
}

export default VoicePlayManager.getInstance();
